import { PitchDetector } from 'pitchy';
import {
  SpectrumAnalyzer,
  applyWindow,
  hannWindow,
  isClipping,
  rms,
  spectralCentroid,
  spectralFlux,
} from './dsp';

/**
 * One analysis frame's worth of measurements.
 *
 * The AudioWorklet and the ScriptProcessor fallback both run this, so the two
 * paths cannot drift apart, and so the whole thing is testable by feeding it
 * buffers rather than a microphone.
 */

export interface FrameAnalyzerOptions {
  sampleRate: number;
  windowSize: number;
  /** Below this the pitch is reported but flagged unreliable. */
  clarityThreshold?: number;
  minFreq?: number;
  maxFreq?: number;
  /**
   * Transform size for the spectrum handed to chord detection, when the caller
   * supplies a buffer that long. Defaults to {@link CHORD_SPECTRUM_SIZE}.
   */
  spectrumSize?: number;
}

/**
 * The spectrum chord detection needs is a longer one than pitch tracking runs
 * on. Tracking wants a short window so a note is reported promptly; a 2048
 * point window at 48 kHz has bins 23 Hz apart, and at C4 the neighboring
 * semitones sit inside C's own window lobe, so a correctly played chord reads
 * as having loud wrong notes in it. 8192 points puts a bin at 5.9 Hz, which
 * is what `matchVoicing` was designed and tested against.
 */
export const CHORD_SPECTRUM_SIZE = 8192;

export interface AnalysisFrame {
  /** Seconds on the audio clock at the end of the window. */
  t: number;
  rms: number;
  /** Hz, or 0 when no pitch could be found. */
  f0: number;
  /** 0–1; how periodic the window was. */
  clarity: number;
  flux: number;
  centroid: number;
  clipping: boolean;
  /** Only present when a spectrum was asked for. */
  mags?: Float32Array;
}

export class FrameAnalyzer {
  readonly sampleRate: number;
  readonly windowSize: number;
  private readonly clarityThreshold: number;
  private readonly minFreq: number;
  private readonly maxFreq: number;

  private readonly window: Float32Array;
  private readonly spectrum: SpectrumAnalyzer;
  private readonly detector: PitchDetector<Float32Array>;
  private previousMags: Float32Array | null = null;
  /** The chord-detection spectrum: its own size, window and transform. */
  readonly spectrumSize: number;
  private readonly longWindow: Float32Array;
  private readonly longSpectrum: SpectrumAnalyzer;

  constructor(options: FrameAnalyzerOptions) {
    this.sampleRate = options.sampleRate;
    this.windowSize = options.windowSize;
    this.clarityThreshold = options.clarityThreshold ?? 0.85;
    this.minFreq = options.minFreq ?? 60;
    this.maxFreq = options.maxFreq ?? 4200;

    this.window = hannWindow(options.windowSize);
    this.spectrum = new SpectrumAnalyzer(options.windowSize);
    this.detector = PitchDetector.forFloat32Array(options.windowSize);
    this.spectrumSize = options.spectrumSize ?? CHORD_SPECTRUM_SIZE;
    this.longWindow = hannWindow(this.spectrumSize);
    this.longSpectrum = new SpectrumAnalyzer(this.spectrumSize);
    // `clarityThreshold` is left at pitchy's default: it is MPM's peak-picking
    // constant, not an output gate, and lowering it makes the detector choose
    // sub-harmonic peaks. Deciding whether a frame is trustworthy is this
    // class's `isReliable`, and gating on the room's noise floor is
    // NoteTracker's job.
  }

  /**
   * Analyzes one window. `wantSpectrum` is off by default because the magnitude
   * array is the expensive thing to hand across a thread boundary, and only
   * chord detection needs it.
   *
   * `longSamples` is the last `spectrumSize` samples, ending where `samples`
   * ends. When it is given, the spectrum comes from it; without it the
   * tracking window is used, which is too coarse for chords (see
   * {@link CHORD_SPECTRUM_SIZE}) but still right for the flux and centroid.
   */
  analyze(
    samples: Float32Array,
    t: number,
    wantSpectrum = false,
    longSamples?: Float32Array,
  ): AnalysisFrame {
    const level = rms(samples);
    const clipping = isClipping(samples);

    // Pitch runs on the raw window: the MPM autocorrelation wants the signal as
    // it is, and a Hann window would taper away the periodicity it looks for.
    const [rawF0, clarity] = this.detector.findPitch(samples, this.sampleRate);
    const inRange = rawF0 >= this.minFreq * 0.9 && rawF0 <= this.maxFreq * 1.1;
    const f0 = inRange ? rawF0 : 0;

    const windowed = applyWindow(samples, this.window);
    const mags = this.spectrum.magnitudes(windowed);
    const flux = spectralFlux(mags, this.previousMags);
    const centroid = spectralCentroid(mags, this.sampleRate, this.windowSize);

    // `magnitudes` reuses its buffer, so the copy kept for the next frame's flux
    // has to be a real copy.
    this.previousMags = Float32Array.from(mags);

    const frame: AnalysisFrame = {
      t,
      rms: level,
      f0,
      clarity: inRange ? clarity : 0,
      flux,
      centroid,
      clipping,
    };
    if (wantSpectrum) {
      frame.mags =
        longSamples !== undefined && longSamples.length === this.spectrumSize
          ? Float32Array.from(
              this.longSpectrum.magnitudes(applyWindow(longSamples, this.longWindow)),
            )
          : Float32Array.from(mags);
    }
    return frame;
  }

  /** True when this frame's pitch is clear enough to act on. */
  isReliable(frame: AnalysisFrame): boolean {
    return frame.f0 > 0 && frame.clarity >= this.clarityThreshold;
  }
}
